import React from "react";
import { Images } from "../../../utils/imageConstants";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import { Api } from "../../../services/interceptor";
import type { ILeagueStats, ITournamentPlayerStats } from "../types/home";
import { useQuery } from "@tanstack/react-query";
import { useAppSelector } from "../../../redux/hooks";
import { selectSelectedLeagueId } from "../../../redux/slice/selectedLeagueSlice";

type BoardHeadingMode = "mvp" | "runs" | "wickets";

type BoardDef = {
  key: string;
  /** Match `ILeagueStats.key` from API (case-insensitive); first match wins */
  matchKeys: string[];
  title: string;
  icon: string;
  headingMode: BoardHeadingMode;
};

const BOARDS: BoardDef[] = [
  {
    key: "mvp",
    matchKeys: ["Most Valuable Player"],
    title: "Most Valuable Player",
    icon: Images.most_valuable_player,
    headingMode: "mvp",
  },
  {
    key: "runs",
    matchKeys: ["Runs"],
    title: "Runs",
    icon: Images.batting_record_icon,
    headingMode: "runs",
  },
  {
    key: "wickets",
    matchKeys: ["Wickets"],
    title: "Wickets",
    icon: Images.most_wickets_icon,
    headingMode: "wickets",
  },
];

function findBoardData(
  leagueStats: ILeagueStats[] | undefined,
  board: BoardDef,
): ILeagueStats | undefined {
  if (!Array.isArray(leagueStats)) return undefined;
  return leagueStats.find((ls) => {
    const k = ls?.key?.trim();
    if (!k) return false;
    const lower = k.toLowerCase();
    return board.matchKeys.some((m) => m.trim().toLowerCase() === lower);
  });
}

function headingColumns(
  board: BoardDef,
  boardData: ILeagueStats | undefined,
): React.ReactNode {
  if (board.headingMode === "mvp") {
    return <div className="column-data">Points</div>;
  }
  if (board.headingMode === "runs") {
    return (
      <>
        <div className="column-data">Mat</div>
        <div className="column-data">Avg</div>
        <div className="column-data">Runs</div>
      </>
    );
  }
  if (board.headingMode === "wickets") {
    return (
      <>
        <div className="column-data">Mat</div>
        <div className="column-data">Wkts</div>
      </>
    );
  }
  return null;
}

const LeagueStats: React.FC = () => {
  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);

  const { isLoading, data: leagueStats } = useQuery<ILeagueStats[]>({
    queryKey: [QUERY_KEY.getLeagueStats, selectedLeagueId],
    enabled: !!selectedLeagueId,
    queryFn: async () => {
      const response = await Api.get(QUERY_KEY.getLeagueStats, {
        params: {
          leagueId: selectedLeagueId,
          topN: "5",
        },
      });
      const data = response?.data;
      return Array.isArray(data) ? data : [];
    },
  });

  return (
    <>
      <div className="league-stats">
        <h1 className="heading">All time League Stats</h1>

        {isLoading ? (
          <div className="stats-grid" style={{ padding: "24px", textAlign: "center", color: "#666" }}>
            Loading league stats…
          </div>
        ) : (
          <div className="stats-grid">
            {BOARDS.map((b) => {
              const boardData = findBoardData(leagueStats, b);
              return (
                <div className="stat-card" key={b.key}>
                  <div className="card-header">
                    <div className="left">
                      <span className="icon">
                        <img src={b.icon} alt="" />
                      </span>
                      <span className="title">{b.title}</span>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="row headings">
                      <div className="row-left">Player</div>
                      <div className="right-columns">{headingColumns(b, boardData)}</div>
                    </div>

                    {boardData?.tournamentPlayerStats && boardData.tournamentPlayerStats.length > 0 ? (
                      boardData.tournamentPlayerStats.map((p: ITournamentPlayerStats, i: number) => (
                        <div className="row" key={i}>
                          <div className="row-left">
                            <div className="avatar">
                              <img
                                src={Images.card_info_image}
                                alt={`${p.playerResponse.playerFirstName} ${p.playerResponse.playerLastName}`}
                              />
                            </div>
                            <div className="who">
                              <div className="name">
                                {p.playerResponse.playerFirstName} {p.playerResponse.playerLastName}
                              </div>
                              <div className="team">{p.teamResponse.teamName}</div>
                            </div>
                          </div>

                          <div className="right-columns">
                            {b.headingMode === "mvp" ? (
                              <div className="column-data">
                                {p.statsList.find((s) => s.key === "Points")?.value}
                              </div>
                            ) : (
                              <div className="multi-column">
                                {p.statsList.map((s) => (
                                  <div key={s.key} className="column-data">
                                    <span className="value">{s.value}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="row" style={{ padding: "16px", textAlign: "center", color: "#666" }}>
                        <div className="row-left">No data available</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default LeagueStats;
